import useVersionManagement from './useVersionManagement';
import { parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Parse design reference sections and extract additional metadata
 * like colors, typography, file info, etc.
 */
const enhanceDesignReferenceSections = (sections) => {
  return sections.map(section => {
    const enhanced = { ...section };

    // Extract colors from Color Palette sections
    const colorMatches = section.content.match(/\*\*([^:]+):\*\*\s*(#[0-9A-Fa-f]{3,6})/g);
    if (colorMatches) {
      enhanced.colors = colorMatches.map(match => {
        const parts = match.match(/\*\*([^:]+):\*\*\s*(#[0-9A-Fa-f]{3,6})/);
        return {
          name: parts[1].trim(),
          hex: parts[2].toUpperCase()
        };
      });
    }

    // Check if this is a file analysis section (has filename in parentheses)
    const fileMatch = section.title.match(/^(.+)\(([^)]+)\)$/);
    if (fileMatch) {
      enhanced.isFileAnalysis = true;
      enhanced.descriptiveName = fileMatch[1].trim();
      enhanced.fileName = fileMatch[2].trim();
    }

    // Check if this is a summary section
    const summaryKeywords = ['summary', 'takeaways', 'overview'];
    enhanced.isSummary = summaryKeywords.some(keyword =>
      section.title.toLowerCase().includes(keyword)
    );

    // Extract file type if present
    const fileTypeMatch = section.content.match(/\*\*File Type:\*\*\s*([^\n]+)/);
    if (fileTypeMatch) {
      enhanced.fileType = fileTypeMatch[1].trim();
    }

    return enhanced;
  });
};

/**
 * Custom hook to manage design reference versions
 * Stores and tracks different versions of design references from regenerations
 */
const useDesignReferenceVersions = (conversation, isStreaming, streamingContent) => {
  // Configuration for design reference version management
  const designReferenceConfig = {
    // Determine if a message contains design references
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return (
        content.includes('# Design References Analysis') ||
        content.includes('## Color Schemes Summary') ||
        content.includes('**Color Palette:**') ||
        content.includes('**Typography:**') ||
        content.includes('**Key Visual Elements:**')
      );
    },

    // Parse design references from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return enhanceDesignReferenceSections(msg.parsed_content.sections);
      }
      const sections = parseMarkdownIntoSections(content);
      return enhanceDesignReferenceSections(sections);
    },

    // Extract version information from a message
    extractVersions: (msg, parsedReferences, versionNumber, isPreview = false) => {
      // Extract overview if present
      const overviewMatch = msg.content?.match(/##\s*Overview\s*([\s\S]*?)(?=---|##|$)/);
      const overview = overviewMatch ? overviewMatch[1].trim() : '';

      // Separate file analyses from summaries
      const fileAnalyses = parsedReferences.filter(ref => ref.isFileAnalysis);
      const summaries = parsedReferences.filter(ref => ref.isSummary);
      const others = parsedReferences.filter(ref => !ref.isFileAnalysis && !ref.isSummary);

      // Collect all colors from all sections
      const allColors = [];
      parsedReferences.forEach(ref => {
        if (ref.colors) {
          allColors.push(...ref.colors);
        }
      });

      // Remove duplicate colors by hex value
      const uniqueColors = [];
      const seenHex = new Set();
      allColors.forEach(color => {
        if (!seenHex.has(color.hex)) {
          seenHex.add(color.hex);
          uniqueColors.push(color);
        }
      });

      return {
        id: msg._id || msg.id || `${msg.createdAt}-${versionNumber}`,
        timestamp: msg.createdAt || new Date().toISOString(),
        references: parsedReferences,
        fileAnalyses,
        summaries,
        otherSections: others,
        overview,
        allColors: uniqueColors,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent
  };

  // Use the generic version management hook
  const versionData = useVersionManagement(designReferenceConfig);

  // Map the generic version data to design reference-specific structure
  return {
    versions: versionData.versions,
    currentVersion: versionData.currentVersion ? {
      ...versionData.currentVersion,
      references: versionData.currentVersion.parsedContent || versionData.currentVersion.references
    } : null,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useDesignReferenceVersions;
